import { useEffect, useRef, useState } from 'react';

import Tracked from '~/components/Tracked';
import type { TrackedButtonClickName } from '~/services/gtm.client';

type Props = {
  children: string;
  isSelected: boolean;
  name: TrackedButtonClickName;
  onClick: () => void;
  shortLabel?: string;
  stretch?: boolean;
};

const labelClassName = 'whitespace-nowrap p-0.5 text-sm font-light';

export default function ViewTab({
  children,
  isSelected,
  name,
  onClick,
  shortLabel,
  stretch = true
}: Props) {
  const labelRef = useRef<HTMLParagraphElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [fullLabelFits, setFullLabelFits] = useState(true);

  useEffect(() => {
    const label = labelRef.current;
    const measure = measureRef.current;

    if (!stretch || !shortLabel || !label || !measure) return;

    const observer = new ResizeObserver(() =>
      setFullLabelFits(measure.offsetWidth <= label.clientWidth)
    );

    observer.observe(label);

    return () => observer.disconnect();
  }, [shortLabel, stretch]);

  return (
    <Tracked.Button
      onClick={onClick}
      className={`relative !rounded-full ${stretch ? 'min-w-0 flex-1 basis-0' : 'max-w-40'}`}
      pagePosition="WTB"
      name={name}
      variant={isSelected ? 'black' : 'white-grey-border'}
    >
      <p
        ref={labelRef}
        className={`flex items-center justify-center overflow-hidden ${labelClassName}`}
      >
        {fullLabelFits || !shortLabel ? children : shortLabel}
      </p>
      {stretch && shortLabel && (
        <span
          ref={measureRef}
          aria-hidden
          className={`invisible absolute left-0 top-0 ${labelClassName}`}
        >
          {children}
        </span>
      )}
    </Tracked.Button>
  );
}
